'use client'

import { useCallback, useEffect, useState } from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import MobileBottomBar from './MobileBottomBar'
import { ChevronDown, Close } from './Icons'

/*
 * Menu bilkul purane project jaisa — labels, unka order, aur dropdown ka naam,
 * sab Master.Master se hu-ba-hu:
 *
 *   Home | About us | Profile (Our Profile, Team Profile) | Practice Areas
 *   | Internship | Contact us
 *
 * Dhyaan dein: "About us" aur "Contact us" me chhota 'u' hai, aur dropdown
 * "Profile" kehlata hai (reference design me "Our Team" tha, wo hata diya).
 * Routes bhi wahi hain, isliye purane links aur Google index chalte rehte hain.
 *
 * Reference me "Blogs" bhi tha, par project me blog ka koi data/table nahi hai,
 * isliye wo nav item nahi rakha — dead link dene se behtar hai na dikhana.
 */
const NAV = [
  { href: '/', label: 'Home' },
  { href: '/About', label: 'About us' },
  {
    label: 'Profile',
    children: [
      { href: '/OurProfile', label: 'Our Profile' },
      { href: '/TeamProfile', label: 'Team Profile' },
    ],
  },
  { href: '/PracticeArea', label: 'Practice Areas' },
  { href: '/Internship', label: 'Internship' },
  { href: '/Contact', label: 'Contact us' },
] as const

/*
 * Desktop par kuch nahi badla — wahi ek line ka menu, wahi hover dropdown.
 *
 * Mobile par (≤1024px) menu ab daayein se aane wala drawer hai. Pehle wo header
 * ke neeche khulne wala panel tha, jo lambe menu par hero ko dhak leta tha aur
 * scroll karte hi adhoora dikhta tha. Drawer poori height leta hai, apne andar
 * scroll karta hai, aur daayein se aata hai — yaani ussi taraf se jahan angootha
 * pehle se rehta hai (hamburger aur bottom bar ka "Menu", dono daayein hain).
 */
export default function SiteHeader({ phone }: { phone: string }) {
  const pathname = usePathname()
  const [open, setOpen] = useState(false)
  // konsa dropdown khula hai (mobile accordion) — desktop par hover chalta hai
  const [openSub, setOpenSub] = useState<string | null>(null)

  const close = useCallback(() => setOpen(false), [])

  // route badalte hi mobile menu apne aap band ho jaaye
  useEffect(() => {
    setOpen(false)
    setOpenSub(null)
  }, [pathname])

  // drawer khula ho to peeche ka page scroll na ho
  useEffect(() => {
    if (!open) return
    const previous = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previous
    }
  }, [open])

  // Esc se band — keyboard aur external keyboard wale tablets ke liye
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false)
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open])

  const isActive = (href: string) =>
    href === '/' ? pathname === '/' : pathname.toLowerCase().startsWith(href.toLowerCase())

  return (
    <header className="site-header">
      <div className="container site-header__inner">
        <Link href="/" className="brand" aria-label="LexNidanam — home">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img src="/img/finalLogo.png" width={290} height={112} alt="LexNidanam" />
        </Link>

        {/*
          Teen lakeerein SVG icon ki jagah CSS se hain — sirf isliye ki wo
          khulne par ghoom kar ✕ ban sakein. SVG me har bar ko alag se ghumana
          padta, aur do alag icons (Menu/Close) swap karne par animation hi
          nahi milti.
        */}
        <button
          type="button"
          className="nav-toggle"
          aria-expanded={open}
          aria-controls="primary-nav"
          onClick={() => setOpen((v) => !v)}
        >
          <span className="nav-toggle__bars" aria-hidden="true">
            <i />
            <i />
            <i />
          </span>
          <span className="sr-only">{open ? 'Menu band karein' : 'Menu kholein'}</span>
        </button>

        {/* drawer ke peeche ka parda — sirf mobile par, tap karne se menu band */}
        <div
          className="nav-backdrop"
          data-open={open}
          onClick={close}
          aria-hidden="true"
        />

        <nav className="nav" id="primary-nav" data-open={open} aria-label="Main">
          {/* drawer ka apna sirhaana — desktop par ye poori tarah chhupa rehta hai */}
          <div className="nav__head">
            <span className="nav__head-title">Menu</span>
            <button type="button" className="nav__close" onClick={close}>
              <Close />
              <span className="sr-only">Menu band karein</span>
            </button>
          </div>

          {NAV.map((item) =>
            'children' in item ? (
              <div
                className="nav__item"
                key={item.label}
                data-open={openSub === item.label}
              >
                <button
                  type="button"
                  className="nav__link nav__link--sub"
                  aria-expanded={openSub === item.label}
                  aria-current={
                    item.children.some((c) => isActive(c.href)) ? 'page' : undefined
                  }
                  onClick={() =>
                    setOpenSub((v) => (v === item.label ? null : item.label))
                  }
                >
                  {item.label}
                  <ChevronDown />
                </button>
                <div className="nav__panel">
                  {item.children.map((child) => (
                    <Link
                      key={child.href}
                      href={child.href}
                      aria-current={isActive(child.href) ? 'page' : undefined}
                    >
                      {child.label}
                    </Link>
                  ))}
                </div>
              </div>
            ) : (
              <Link
                key={item.href}
                href={item.href}
                className="nav__link"
                aria-current={isActive(item.href) ? 'page' : undefined}
              >
                {item.label}
              </Link>
            )
          )}
        </nav>
      </div>

      {/*
        Bottom bar header ke andar hi rakhi hai (position:fixed hai, isliye dikhti
        neeche hai) — taaki uska "Menu" button upar wale hamburger ki hi state
        chalaye, do alag menu na ban jaayein.
      */}
      <MobileBottomBar
        phone={phone}
        open={open}
        onMenuToggle={() => setOpen((v) => !v)}
        isActive={isActive}
      />
    </header>
  )
}
